import { Component, OnInit, inject } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Command, LabelCommand } from "@tylertech/commands-module";
import { isHttpResponseError, SecurityService, ToastService } from "@tylertech/core-module";
import { ExecutionResult, GetNoteObjectViewModel, INote, NoteViewModel } from "@tylertech/tyler-api-types";
import { createNote } from "../../components";
import { NoteService } from "../noteService.g";

export interface NotesDialogData
{
    readonly hideSubject: boolean;
    readonly isAdd: boolean;
    readonly newParentItem: boolean;
    readonly passedNotes: Array<INote>;
    readonly settings: GetNoteObjectViewModel;
    readonly singleNote: boolean;
    readonly viewOnly: boolean;
}

@Component({
    selector: "notes-dialog-component",
    templateUrl: "./notesDialog.tmpl.html",
    standalone: false
})
export class NotesDialogComponent
    implements OnInit
{
    private readonly dialogRef = inject<MatDialogRef<NotesDialogComponent>>(MatDialogRef);
    private readonly data = inject<NotesDialogData>(MAT_DIALOG_DATA);
    private readonly $securityService = inject(SecurityService);
    private readonly $toastService = inject(ToastService);
    private readonly $noteService = inject(NoteService);

    readonly commands: Array<Command>;
    readonly formGroup = new FormGroup({});

    /** @deprecated - NEVER READ */
    protected deleteError: boolean;
    /** @deprecated - NEVER READ */
    protected saveError: boolean;
    private originalNoteCount: number = 0;
    protected deleteComplete: boolean;
    protected saveComplete: boolean;

    isLoaded: boolean;
    isProcessing: boolean;
    notes: Array<INote>;
    protected savingPromise: Promise<void>;

    constructor()
    {
        this.commands = [
            new LabelCommand({
                type: "outlined",
                label: "Cancel",
                action: () => this.close(),
                getVisible: () => !this.viewOnly,
            }),
            new LabelCommand({
                type: "outlined",
                label: "Save & Close",
                action: () => this.save(),
                getVisible: () => !this.viewOnly,
                getEnabled: () => this.formGroup.valid,
            }),
            new LabelCommand({
                type: "raised",
                label: "Close",
                action: () => this.close(),
                getVisible: () => this.viewOnly,
            }),
        ];
    }

    private get isAdd(): boolean { return this.data.isAdd; }
    private get newParentItem(): boolean { return this.data.newParentItem; }
    private get passedNotes(): Array<INote> { return this.data.passedNotes; }
    private get settings(): GetNoteObjectViewModel { return this.data.settings; }

    get canAdd(): boolean
    {
        return !this.viewOnly && !this.isSingleNote;
    }

    get hideSubject(): boolean { return this.data.hideSubject; }
    get isSingleNote(): boolean { return this.data.singleNote; }
    get viewOnly(): boolean { return this.data.viewOnly; }

    get note(): INote
    {
        return this.notes?.length !== 0
            ? this.notes[0]
            : undefined;
    }

    ngOnInit(): void
    {
        if (!this.isAdd && !this.newParentItem)
        {
            this.getNotes();
            return;
        }

        if (!this.passedNotes)
        {
            this.notes = [];
            this.addNote();
            return;
        }

        // FIXME(DO-7969) - This converts the CreateDate Date object to a string representation.
        this.notes = this.passedNotes.map(x => JSON.parse(JSON.stringify(x)));

        this.originalNoteCount = this.passedNotes.length;

        this.isLoaded = true;
    }

    addNote(): void
    {
        this.isLoaded = true;
        this.notes.unshift(createNote(this.$securityService.principle.Fullname));

        // TODO - why is this in a timeout... and why are we doing DOM operations
        setTimeout(
            () =>
            {
                // TODO - we should just have a @ViewChild("mat-dialog-content") field and interact with that
                const element = document.getElementsByClassName("note-container");
                element[0].scrollTop = element[0].scrollHeight;
                element[0].scrollTo({
                    top: 0,
                    behavior: "smooth",
                });
            },
            100);
    }

    close(): void
    {
        // FIXME(DO-7969) - why are we doing this? When we add notes, we prepend.. this will NOT remove new notes from the notes array....
        this.notes.length = this.originalNoteCount;

        this.dialogRef.close(this.passedNotes
            ?? this.notes);
    }

    onDelete(note: INote): void
    {
        if (!note.IsNew)
        {
            note.IsDeleted = true;
            return;
        }

        this.notes = this.notes.filter(x => x !== note);
    }

    protected getNonSgsDeleteResults(note: INote): Promise<ExecutionResult<boolean>>
    {
        return this.$noteService.Delete(
            {
                Id: note.Id,
                Type: this.settings.Type,
            })
            .then(
                (result: ExecutionResult<boolean>) =>
                {
                    this.notes = this.notes.filter(x => x !== note);
                    result.Success = true;

                    return result;
                },
                (result: unknown) =>
                {
                    if (isHttpResponseError(result))
                    {
                        this.$toastService.showToast(`Note "${note.Subject}": ${result.error.Message}`);
                    }

                    this.deleteError = true;

                    return { Success: false };
                });
    }

    protected getNonSgsSaveResults(note: INote): Promise<ExecutionResult<boolean>>
    {
        return this.$noteService.Save(
            {
                note: note as NoteViewModel,
                parentId: this.settings.Id,
                Type: this.settings.Type,
            })
            .then(
                (result) =>
                {
                    // FIXME(DO-7969) - Saving a single note shouldn't be mutating the whole list.
                    this.notes.forEach(
                        x =>
                        {
                            x.IsNew = false;
                        });

                    // TODO - why is the backend sending the Success flag already set?
                    result.Success = true;

                    return result;
                },
                (result: unknown) =>
                {
                    if (isHttpResponseError(result))
                    {
                        this.$toastService.showToast(`Note "${note.Subject}": ${result.error.Message}`);
                    }

                    this.saveError = true;

                    return { Success: false };
                });
    }

    private getNotes(): void
    {
        this.$noteService.Get(this.settings)
            .then((executionResult: ExecutionResult<Array<INote>>) =>
            {
                const result = executionResult.Result;
                if (!result)
                {
                    this.notes = [];
                    this.originalNoteCount = 0;
                }
                else
                {
                    this.notes = result;
                    this.originalNoteCount = result.length;
                }

                if (this.notes.length === 0 && !this.viewOnly)
                {
                    this.addNote();
                }

                this.isLoaded = true;
            });
    }

    protected hideAfterSave(): void
    {
        if (this.deleteComplete
            && this.saveComplete)
        {
            this.dialogRef.close(this.notes);
        }
    }

    protected newNote(toSave: Array<INote>): Promise<void>
    {
        return this.$noteService.Save(
            {
                note: toSave[0] as NoteViewModel,
                parentId: this.settings.Id,
                Type: this.settings.Type,
            })
            .then(result =>
            {
                if (!result)
                {
                    this.saveError = true;
                    return;
                }

                this.saveComplete = true;
                // FIXME(DO-7969) - Why are we working on the instance field instead of the list passed in?
                this.notes.forEach(x => (x.IsNew = false));
                this.hideAfterSave();
                return;
            });
    }

    save(): void
    {
        if (this.notes.length === 0)
        {
            this.dialogRef.close(this.notes);
            return;
        }

        if (!this.validateNotes())
        {
            return;
        }

        if (this.newParentItem)
        {
            this.dialogRef.close(this.notes);
            return;
        }

        const toDelete = this.notes.filter(x => x.IsDeleted);
        const toSave = this.notes.filter(x => !x.IsDeleted);
        if (toDelete.length === 0)
        {
            this.deleteComplete = true;
        }

        if (toSave.length === 0)
        {
            this.saveComplete = true;
        }

        if (toSave.length === 1 && toSave[0].IsNew === true && this.deleteComplete)
        {
            this.savingPromise = this.newNote(toSave)
                .finally(() =>
                {
                    this.savingPromise = undefined;
                });
            return;
        }

        this.isProcessing = true;
        this.savingPromise = (this.hideSubject
            ? this.sgsSave(toSave, toDelete)
            : this.nonSgsSave(toSave, toDelete))
            .finally(() =>
            {
                this.isProcessing = false;
                this.savingPromise = undefined;
            });
    }

    protected nonSgsSave(toSave: Array<INote>, toDelete: Array<INote>): Promise<void>
    {
        const serviceCalls: Array<Promise<ExecutionResult<boolean>>> = [];

        toDelete.forEach(note =>
        {
            return serviceCalls.push(
                this.getNonSgsDeleteResults(note));
        });

        toSave.forEach(note =>
        {
            return serviceCalls.push(
                this.getNonSgsSaveResults(note));
        });

        return Promise.all(serviceCalls)
            .then(results =>
            {
                if (results.filter(result => !result.Success).length === 0)
                {
                    this.saveComplete = true;
                    this.deleteComplete = true;
                    this.hideAfterSave();
                }
            });
    }

    protected sgsSave(toSave: Array<INote>, toDelete: Array<INote>): Promise<void>
    {
        if (toSave.length === 0)
        {
            return this.deleteNotes(toDelete)
                .then((complete) =>
                {
                    if (!complete)
                    {
                        return;
                    }

                    this.deleteComplete = true;
                    this.hideAfterSave();
                });
        }

        let i = 0;
        const notesToSave: Array<INote> = [];
        const self = this;

        // TODO - Why do we need to re-order the note list so that the new notes are last?
        for (let q = toSave.length - 1; q >= 0; q--)
        {
            if (!toSave[q].IsNew)
            {
                notesToSave.push(toSave[q]);
            }
        }

        for (let p = 0; p < toSave.length; p++)
        {
            if (toSave[p].IsNew)
            {
                notesToSave.push(toSave[p]);
            }
        }

        return this.$noteService.Delete(
            {
                // TODO - what aren't we deleting something from toDelete?
                Id: this.notes[0].Id,
                Type: this.settings.Type,
            })
            .then(result =>
            {
                if (!result)
                {
                    this.saveError = true;
                    return;
                }

                this.deleteComplete = true;

                return this.$noteService.Save(
                    {
                        note: notesToSave[i] as NoteViewModel,
                        parentId: this.settings.Id,
                        Type: this.settings.Type,
                    })
                    .then(onSuccess);
            });

        function onSuccess(result: ExecutionResult<boolean>): Promise<void> | void
        {
            if (!result)
            {
                self.saveError = true;
                return;
            }

            if (notesToSave.length !== i + 1)
            {
                i++;

                return self.$noteService.Save(
                    {
                        note: notesToSave[i] as NoteViewModel,
                        parentId: self.settings.Id,
                        Type: self.settings.Type,
                    })
                    .then(onSuccess);
            }

            if (notesToSave.length === i + 1)
            {
                self.saveComplete = true;
                self.notes = notesToSave;
                self.hideAfterSave();
            }
        }
    }

    protected deleteNotes(toDelete: Array<INote>): Promise<boolean>
    {
        // FIXME(DO-7969) - what about the rest of the array?
        return this.$noteService.Delete(
            {
                Id: toDelete[0].Id,
                Type: this.settings.Type,
            })
            .then(result =>
            {
                if (!result)
                {
                    this.saveError = true;
                    return false;
                }

                for (let i = 0; i < toDelete.length; i++)
                {
                    this.notes = this.notes.filter(x => x !== toDelete[i]);
                }
                return true;
            });
    }

    protected validateNotes(): boolean
    {
        if (!this.notes?.length)
        {
            return false;
        }

        const notes = this.notes;
        const noteCount = notes.length;

        const validNotes = notes.filter(
            (note: INote) =>
            {
                return this.validateNote(note);
            });

        return validNotes.length === noteCount;
    }

    protected validateNote(note: INote): boolean
    {
        if (note.IsDeleted)
        {
            return true;
        }

        if (!note.Description)
        {
            return false;
        }

        if (this.isSingleNote)
        {
            return true;
        }

        if (!note.CreatedBy && !this.hideSubject)
        {
            return false;
        }

        if (!note.CreatedDate)
        {
            return false;
        }

        if (!note.Subject && !this.hideSubject)
        {
            return false;
        }

        return true;
    }
}
